import React, { useMemo } from "react";
import { uniqBy } from "lodash";
import classNames from "classnames";
import { useFinancialAccounts } from "../../../hooks/useFinancialAccounts";
import { getLeadingAccountConnection } from "../../../common/helpers/financialAccount";
import { IntegrationConnectionIcon } from "../../financialConnection/FinancialConnectionIcon";
import { getConnectionLabel } from "../../../common/helpers/financialConnection";
import { ObjectIcon } from "../ObjectIcon/ObjectIcon";
import { DotsHorizontalIcon } from "../../../icons";
import { BreakableText } from "../BreakableText/BreakableText";
import { getAnonymousAccountNumber } from "../../../common/helpers/bankAccount";
import { FinancialAccountType } from "../../../common/types/financialAccount";
import {
    getIntegrationConnectionInstitutionName,
    isIntegrationConnection,
} from "../../../common/helpers/integrationConnection";
import styles from "./AccountsFilter.module.scss";

interface Props {
    isAccountSelected: (accountId: number, entityId: number) => boolean;
    showSelectedIcons: boolean;
    isSingleSelect?: boolean;
}

const ICONS_LIMIT = 4;

export const AccountsFilterAccountValueDisplay: React.FC<Props> = ({
    isAccountSelected,
    showSelectedIcons,
    isSingleSelect,
}) => {
    const accounts = useFinancialAccounts({ createTempCashAccounts: true });
    const selectedAccounts = useMemo(
        () =>
            accounts.filter(
                (account) =>
                    account.entityId &&
                    isAccountSelected(account.id, account.entityId),
            ),
        [accounts, isAccountSelected],
    );

    if (selectedAccounts.length === 0) {
        return <>Select account{!isSingleSelect ? "s" : ""}</>;
    }

    if (selectedAccounts.length === 1) {
        const account = selectedAccounts[0];
        const connection = getLeadingAccountConnection(account);
        const accountName =
            account.financialType === FinancialAccountType.CASH
                ? `${account.name} - ${account.entity?.name}`
                : account.name;
        return (
            <div className="d-flex gap-2 align-items-center">
                {showSelectedIcons && (
                    <div
                        className={classNames(
                            styles.icon,
                            "d-flex align-items-center",
                        )}
                    >
                        <IntegrationConnectionIcon
                            account={account}
                            connection={connection ?? undefined}
                        />
                    </div>
                )}
                <span>
                    {account.accountNumberMask && (
                        <span>
                            {getAnonymousAccountNumber(
                                account.accountNumberMask,
                                4,
                            )}
                        </span>
                    )}
                    {!account.accountNumberMask && (
                        <BreakableText text={accountName} />
                    )}
                </span>
            </div>
        );
    }

    const accountsUniqueByConnection = uniqBy(selectedAccounts, (account) => {
        const connection = getLeadingAccountConnection(account);

        return (
            connection &&
            (isIntegrationConnection(connection)
                ? getIntegrationConnectionInstitutionName(connection)
                : getConnectionLabel(connection))
        );
    });

    return (
        <>
            {showSelectedIcons && (
                <div className={classNames(styles.iconList, "d-flex")}>
                    {accountsUniqueByConnection
                        .slice(0, ICONS_LIMIT)
                        .map((account) => (
                            <div
                                className={classNames(
                                    styles.icon,
                                    "d-flex align-items-center",
                                )}
                                key={account.id}
                            >
                                <IntegrationConnectionIcon
                                    account={account}
                                    connection={
                                        getLeadingAccountConnection(account) ??
                                        undefined
                                    }
                                />
                            </div>
                        ))}
                    {accountsUniqueByConnection.length > ICONS_LIMIT && (
                        <ObjectIcon name="..." defaultColor="#ffffff" size="xs">
                            <DotsHorizontalIcon className="accounts-filter__more-icon" />
                        </ObjectIcon>
                    )}
                </div>
            )}
            <span>{selectedAccounts.length} accounts</span>
        </>
    );
};
